import { useTranslation } from 'react-i18next';

import type { DropdownItemProps } from '@innovamat/glimmer-components';
import { STATES } from '@innovamat/glimmer-components';
import { useOrganizationQuery } from '@innovamat/glow-api-client';

import { useCheckIsUpdatePeriod } from '../../components/update-period-announcement-panel';
import type { ImportType } from '../../csv-import';
import { useImportCSV } from '../../csv-import';
import { useUser } from '../../user-management';

type Props = {
  addTeacherButtonText?: string;
  type: ImportType;
  onAddDefault?: () => void;
  onAddDemoStudent?: () => void;
};

type TypeToOptionDict = {
  [key: string]: {
    importDefaultOption?: DropdownItemProps;
    importDemoOption?: DropdownItemProps;
    importGoogleOption?: DropdownItemProps;
    importCSVOption?: DropdownItemProps;
    importClassroomCSVOption?: DropdownItemProps;
    importTeacherCSVOption?: DropdownItemProps;
    importStudentCSVOption?: DropdownItemProps;
  };
};

function useAddActionDropdown({
  addTeacherButtonText,
  type,
  onAddDefault,
  onAddDemoStudent,
}: Props): {
  getOptions: () => DropdownItemProps[];
} {
  const { t } = useTranslation();
  const { goToImportCSVPage, showImportCSV, organizationId } = useImportCSV();
  const { isRole } = useUser();

  const { data: { organization } = {} } = useOrganizationQuery(
    { id: organizationId },
    { staleTime: Infinity, enabled: !!organizationId }
  );

  const isUpdatePeriod = useCheckIsUpdatePeriod();

  const isOrgAnonymized = organization?.anonymized ?? false;

  const typeToOption: TypeToOptionDict = {
    newImport: {
      importClassroomCSVOption: {
        children: t('imports.new_class'),
        icon: 'ClassIcon',
        onSelectItem: () => goToImportCSVPage('classroom'),
        state: isUpdatePeriod ? STATES.DISABLED : STATES.ACTIVE,
      },
      importTeacherCSVOption: {
        children: t('imports.new_teacher'),
        icon: 'TeacherIcon',
        onSelectItem: () => goToImportCSVPage('teacher'),
        state: STATES.ACTIVE,
      },
      importStudentCSVOption: {
        children: t('imports.new_student'),
        icon: 'StudentsIcon',
        onSelectItem: () => goToImportCSVPage('student'),
        state:
          isUpdatePeriod || isOrgAnonymized ? STATES.DISABLED : STATES.ACTIVE,
      },
    },
    schoolStudent: {
      importCSVOption: {
        children: t('school.button.importCSV'),
        onSelectItem: () => goToImportCSVPage('student'),
        icon: 'FileCsvIcon',
        state: STATES.ACTIVE,
      },
    },
    student: {
      importDefaultOption: {
        children: t('students.button.addStudent'),
        onSelectItem: onAddDefault,
        icon: 'AddIcon',
        state: STATES.ACTIVE,
      },
      importDemoOption: {
        children: t('students.button.addDemoStudent'),
        onSelectItem: onAddDemoStudent,
        icon: 'SampleStudentIcon',
        state: onAddDemoStudent ? STATES.ACTIVE : STATES.DISABLED,
      },
      importCSVOption: {
        children: t('school.button.importCSV'),
        onSelectItem: () => goToImportCSVPage(type),
        icon: 'FileCsvIcon',
        state: !isRole.Admin ? STATES.DISABLED : STATES.ACTIVE,
      },
    },
    classroom: {
      importDefaultOption: {
        children: t('classrooms.addAClass'),
        onSelectItem: onAddDefault,
        icon: 'AddIcon',
        state: STATES.ACTIVE,
      },
      importCSVOption: {
        children: t('school.button.importCSV'),
        onSelectItem: () => goToImportCSVPage(type),
        icon: 'FileCsvIcon',
        state: STATES.ACTIVE,
      },
    },
    teacher: {
      importDefaultOption: {
        children: addTeacherButtonText || '',
        onSelectItem: onAddDefault,
        icon: 'AddIcon',
        state: STATES.ACTIVE,
      },
      importCSVOption: {
        children: t('school.button.importCSV'),
        onSelectItem: () => goToImportCSVPage(type),
        icon: 'FileCsvIcon',
        state: STATES.ACTIVE,
      },
    },
  };

  const getOptions = (): DropdownItemProps[] => {
    if (type === 'schoolStudent') {
      return [{ ...typeToOption[type].importCSVOption }] as DropdownItemProps[];
    }

    if (type === 'newImport') {
      return [
        { ...typeToOption[type].importClassroomCSVOption! },
        { ...typeToOption[type].importTeacherCSVOption! },
        { ...typeToOption[type].importStudentCSVOption! },
      ];
    }

    let options: DropdownItemProps[] = [
      { ...typeToOption[type].importDefaultOption! },
    ];

    if (onAddDemoStudent) {
      options = [...options, { ...typeToOption[type].importDemoOption! }];
    }

    if (showImportCSV && !isOrgAnonymized) {
      options = [...options, { ...typeToOption[type].importCSVOption! }];
    }

    return options;
  };

  const filterDisabledOptions = (): DropdownItemProps[] =>
    getOptions().filter((option) => option.state !== STATES.DISABLED);

  return { getOptions: filterDisabledOptions };
}

export { useAddActionDropdown };
